/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Device-linking pane. Mirrors WhatsApp's "linked devices" flow: the
// new device shows a handshake QR, the existing device scans it,
// builds + HPKE-seals + uploads a `LinkingTicket`, and shows a second
// QR that the new device redeems. No BE polling channel required —
// two scans is the worst case, paste-fallback for both sides covers
// every other case.
//
// Handshake QR payload (JSON, plus an optional `t` field carrying the
// ticket id on the second QR):
//
//   { "v": 1, "deviceId": <base64url>, "pubkey": <base64url X25519>,
//     "keyPackage": <base64url MLS KeyPackage> }   // handshake
//   { "v": 1, "t": <ticket_id> }                    // ticket QR
//
// Host wires platform-specific QR rendering + scanning:
//   - Web:   `qrcode` + `html5-qrcode`
//   - macOS: `react-native-qrcode-svg` for render; scanning falls
//            back to paste-only until a vision-camera module lands.

import { CheckCircle2, ClipboardPaste, Link2, QrCode, ScanLine } from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';

import { SettingsDivider, SettingsRow } from '../SettingsRow';

// --- handshake QR payload -------------------------------------------------

export interface LinkingHandshakePayload {
  v: 1;
  deviceId: string;
  pubkey: string;
  keyPackage: string;
}

export interface LinkingTicketQrPayload {
  v: 1;
  t: string;
}

export function isHandshakePayload(o: unknown): o is LinkingHandshakePayload {
  return (
    typeof o === 'object' &&
    o !== null &&
    (o as { v?: unknown }).v === 1 &&
    typeof (o as { deviceId?: unknown }).deviceId === 'string' &&
    typeof (o as { pubkey?: unknown }).pubkey === 'string' &&
    typeof (o as { keyPackage?: unknown }).keyPackage === 'string'
  );
}

export function isTicketPayload(o: unknown): o is LinkingTicketQrPayload {
  return (
    typeof o === 'object' &&
    o !== null &&
    (o as { v?: unknown }).v === 1 &&
    typeof (o as { t?: unknown }).t === 'string'
  );
}

export function encodeTicketQr(ticketId: string): string {
  const payload: LinkingTicketQrPayload = { v: 1, t: ticketId };
  return JSON.stringify(payload);
}

// --- pane interface -------------------------------------------------------

export interface LinkingBuildInput {
  readonly newDeviceIdBase64Url: string;
  readonly newDevicePubBase64Url: string;
  readonly newDeviceKeyPackageBase64Url: string;
}

export interface LinkingBuildResult {
  readonly ticketId: string;
  readonly expiresAt: number;
  /**
   * Pre-auth WS delivery count reported by the BE. The pairing
   * card uses this to switch its primary affordance:
   *   - undefined / >0 : show the "Pairing your new device…"
   *                      auto-pair card, fallback collapsed
   *   - 0 or -1        : auto-expand the legacy pairing-code QR
   *                      because the push will never reach the
   *                      new device (no subscriber, or pusher
   *                      disabled server-side)
   */
  readonly pushDelivered?: number;
}

export interface LinkingAcceptInput {
  readonly ticketId: string;
}

export interface LinkingPaneProps {
  readonly onBuildTicket: (input: LinkingBuildInput) => Promise<LinkingBuildResult>;
  readonly onAcceptTicket?: (input: LinkingAcceptInput) => Promise<void>;
  /**
   * Host-injected QR renderer. Returns a JSX element that paints the
   * given string as a QR code at the requested size (pixels). Web
   * implementations use `qrcode` (canvas/SVG); RN uses
   * `react-native-qrcode-svg`. When omitted the pane hides the QR
   * affordance and falls back to text/paste only.
   */
  readonly renderQrCode?: (value: string, size: number) => JSX.Element;
  /**
   * Host-injected camera scanner trigger. Returning a string resolves
   * the request with the scanned payload (raw text — the pane parses
   * + validates JSON shape). Returning null means the user dismissed
   * the scanner. When omitted scans are unavailable and only paste
   * input is shown.
   */
  readonly onScanRequest?: () => Promise<string | null>;
  /**
   * Optional: poll the BE until the new device confirms its
   * registration (via `/v1/auth/link-verify`), then flip the pane
   * to a "✓ Paired" success state. The pane invokes this after
   * `onBuildTicket` resolves and gives it an `AbortSignal` to honour
   * for cleanup (component unmount, user cancels). Resolves with the
   * confirmed device label when detected, or `null` when the watcher
   * timed out / was cancelled — the pane keeps "Pairing…" in the
   * latter case so the user can still rely on the fallback flow.
   *
   * When omitted the pane stays on "Pairing…" forever (legacy
   * behaviour) — hosts that don't want realtime confirmation can
   * just skip it.
   */
  readonly onWatchForPaired?: (
    input: { newDeviceIdBase64Url: string },
    signal: AbortSignal,
  ) => Promise<{ label: string } | null>;
}

// --- pane -----------------------------------------------------------------

export function LinkingPane(props: LinkingPaneProps): JSX.Element {
  const { onBuildTicket, onAcceptTicket, renderQrCode, onScanRequest, onWatchForPaired } = props;

  // --- existing-device side (issue) ---------------------------------------

  const [handshakeRaw, setHandshakeRaw] = useState('');
  const [handshakeError, setHandshakeError] = useState<string | null>(null);
  const [buildPending, setBuildPending] = useState(false);
  const [buildResult, setBuildResult] = useState<LinkingBuildResult | null>(null);
  // Set when `onBuildTicket` resolves — used by the post-build
  // watcher effect to ask the host to poll `/v1/auth/sessions`
  // until the new device finishes calling `/v1/auth/link-verify`.
  // Kept in state (not just a ref) so a re-mount re-runs the watcher.
  const [paireeDeviceId, setPaireeDeviceId] = useState<string | null>(null);
  // Confirmed pairing — populated by `onWatchForPaired` resolving
  // with a label. When non-null the pane flips its in-progress
  // copy to a "Paired ✓" success state with the device label.
  const [pairedLabel, setPairedLabel] = useState<string | null>(null);
  // Hide the "paste handshake JSON" field by default — scanning is the
  // primary flow; advanced/manual entry is collapsed under a toggle.
  const [showAdvanced, setShowAdvanced] = useState(false);
  // After the existing device builds + uploads the sealed ticket
  // we tell the BE to push it over the pre-auth WS to the new
  // device, so most of the time the user doesn't need the second
  // QR at all. Default the legacy QR fallback to hidden so the UX
  // matches the promise ("you scanned, it's pairing"). The
  // disclosure expands it for the small fraction of cases where
  // the push didn't deliver (firewalled WS, offline relay, etc.).
  const [showFallbackCode, setShowFallbackCode] = useState(false);
  // Auto-expand the fallback when the BE tells us the push didn't
  // reach anyone (no subscriber yet, or pusher disabled server-
  // side). Skips the user having to wonder why nothing happens.
  const pushDelivered = buildResult?.pushDelivered;
  const pushUndelivered =
    pushDelivered === 0 || pushDelivered === -1;
  const fallbackOpen = showFallbackCode || pushUndelivered;

  const tryParseHandshake = (raw: string): LinkingHandshakePayload | null => {
    setHandshakeError(null);
    try {
      const parsed = JSON.parse(raw.trim());
      if (!isHandshakePayload(parsed)) {
        setHandshakeError('Not a valid Ping handshake QR.');
        return null;
      }
      return parsed;
    } catch {
      setHandshakeError('Could not parse handshake JSON.');
      return null;
    }
  };

  const submitHandshake = useCallback(
    async (h: LinkingHandshakePayload) => {
      setBuildPending(true);
      setBuildResult(null);
      setPairedLabel(null);
      setPaireeDeviceId(null);
      try {
        const result = await onBuildTicket({
          newDeviceIdBase64Url: h.deviceId,
          newDevicePubBase64Url: h.pubkey,
          newDeviceKeyPackageBase64Url: h.keyPackage,
        });
        setBuildResult(result);
        // Cache the new device id so the post-build watcher effect
        // knows which device id to look for in `/v1/auth/sessions`.
        setPaireeDeviceId(h.deviceId);
        setHandshakeRaw('');
      } catch (err) {
        setHandshakeError((err as Error).message);
      } finally {
        setBuildPending(false);
      }
    },
    [onBuildTicket],
  );

  // Realtime watcher: once we've uploaded a ticket and know which
  // `new_device_id` it targeted, ask the host to poll the BE until
  // the new device finishes calling `/v1/auth/link-verify`. The
  // host implements the polling (see `handleWatchForPaired` in
  // `SettingsModalHost.tsx`). On success we flip the pairing card
  // to a "Paired ✓ <label>" state so the user sees the result
  // without having to refresh the Sessions tab.
  useEffect(() => {
    if (paireeDeviceId === null || onWatchForPaired === undefined) return;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const result = await onWatchForPaired(
          { newDeviceIdBase64Url: paireeDeviceId },
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        if (result !== null) {
          setPairedLabel(result.label);
        }
      } catch {
        /* watcher rejected — keep the pane on "Pairing…" copy.
           The user still has the fallback pairing-code path. */
      }
    })();
    return () => ctrl.abort();
  }, [paireeDeviceId, onWatchForPaired]);

  const handleScanForHandshake = useCallback(async () => {
    if (!onScanRequest) return;
    const text = await onScanRequest();
    if (text == null) return;
    const h = tryParseHandshake(text);
    if (h) void submitHandshake(h);
  }, [onScanRequest, submitHandshake]);

  const handlePasteHandshake = useCallback(() => {
    const h = tryParseHandshake(handshakeRaw);
    if (h) void submitHandshake(h);
  }, [handshakeRaw, submitHandshake]);

  // --- new-device side (redeem) -------------------------------------------

  const [ticketIdRaw, setTicketIdRaw] = useState('');
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptPending, setAcceptPending] = useState(false);
  const [acceptDone, setAcceptDone] = useState(false);

  const submitTicket = useCallback(
    async (ticketId: string) => {
      if (!onAcceptTicket) return;
      setAcceptPending(true);
      setAcceptError(null);
      try {
        await onAcceptTicket({ ticketId });
        setAcceptDone(true);
        setTicketIdRaw('');
      } catch (err) {
        setAcceptError((err as Error).message);
      } finally {
        setAcceptPending(false);
      }
    },
    [onAcceptTicket],
  );

  const handleScanForTicket = useCallback(async () => {
    if (!onScanRequest) return;
    const text = await onScanRequest();
    if (text == null) return;
    // Accept both `{v:1,t:<id>}` JSON and bare ticket-id strings.
    let ticketId = text.trim();
    try {
      const parsed = JSON.parse(ticketId);
      if (isTicketPayload(parsed)) ticketId = parsed.t;
    } catch {
      // bare string is fine
    }
    void submitTicket(ticketId);
  }, [onScanRequest, submitTicket]);

  const handlePasteTicket = useCallback(() => {
    if (!ticketIdRaw.trim()) {
      setAcceptError('Paste the ticket id from the existing device.');
      return;
    }
    void submitTicket(ticketIdRaw.trim());
  }, [ticketIdRaw, submitTicket]);

  // --- render -------------------------------------------------------------

  const expiresLabel =
    buildResult !== null
      ? new Date(buildResult.expiresAt * 1000).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  return (
    <View className="flex flex-col gap-6">
      <SettingsRow
        variant="hero"
        title="Linked devices"
        description="Pair another phone, tablet, or computer to this account. Pairing is end-to-end encrypted — Ping's servers never see the keys."
        testID="settings-linking-overview"
      />

      {/* Existing-device side — "scan the new device" */}
      <View testID="settings-linking-link-device" className="flex flex-col gap-3">
        <Text className="font-sans font-semibold text-[15px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">
          Link a new device
        </Text>
        <Text className="font-sans text-[13px] leading-[18px] text-light-text-secondary dark:text-[#B5BAC3]">
          Open Ping on the new device, choose <Text className="font-semibold">Link this device</Text>, then point its camera here.
        </Text>

        {buildResult === null ? (
          <View className="flex flex-col gap-3 mt-1">
            {onScanRequest != null && (
              <Pressable
                onPress={handleScanForHandshake}
                disabled={buildPending}
                accessibilityRole="button"
                accessibilityLabel="ping_linking_scan_new_device"
                className="
                  flex flex-row items-center justify-center gap-2
                  py-3 rounded-[12px]
                  bg-light-accent-brand dark:bg-dark-accent-brand
                  active:opacity-80
                "
              >
                <ScanLine size={18} strokeWidth={2} color="#FFFFFF" />
                <Text className="font-sans font-semibold text-[14px] leading-[20px] text-light-text-onAccent dark:text-dark-text-onAccent">
                  {buildPending ? 'Building pairing code…' : 'Scan QR from new device'}
                </Text>
              </Pressable>
            )}

            <Pressable
              onPress={() => setShowAdvanced((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel="ping_linking_toggle_advanced"
              className="self-center"
            >
              <Text className="font-sans text-[12px] leading-[16px] text-light-text-tertiary dark:text-[#6B7280] underline">
                {showAdvanced ? 'Hide manual entry' : 'Or paste handshake manually'}
              </Text>
            </Pressable>

            {showAdvanced && (
              <View
                className="
                  flex flex-col gap-2 p-3 rounded-[12px]
                  bg-light-surface-primary dark:bg-[#0F1117]
                  border border-light-border-secondary dark:border-[#22242A]
                "
              >
                <Text className="font-sans text-[11px] leading-[15px] text-light-text-tertiary dark:text-[#6B7280]">
                  Handshake JSON from the new device
                </Text>
                <TextInput
                  value={handshakeRaw}
                  onChangeText={setHandshakeRaw}
                  autoCapitalize="none"
                  autoCorrect={false}
                  multiline
                  placeholder='{"v":1,"deviceId":"…","pubkey":"…","keyPackage":"…"}'
                  placeholderTextColor="#6B7280"
                  className="
                    font-mono text-[12px] leading-[16px] min-h-[64px]
                    px-3 py-2 rounded-[8px]
                    bg-light-surface-secondary dark:bg-[#15171C]
                    border border-light-border-secondary dark:border-[#2F333B]
                    text-light-text-primary dark:text-[#F5F6F8]
                  "
                />
                <Pressable
                  onPress={handlePasteHandshake}
                  disabled={buildPending || handshakeRaw.length === 0}
                  accessibilityRole="button"
                  accessibilityLabel="ping_linking_build_from_paste"
                  className="
                    self-end flex flex-row items-center gap-2
                    px-4 py-2 rounded-pill
                    bg-light-surface-secondary dark:bg-[#15171C]
                    border border-light-border-secondary dark:border-[#2F333B]
                    active:opacity-70
                  "
                >
                  <Link2 size={14} strokeWidth={1.75} color="#B5BAC3" />
                  <Text className="font-sans font-medium text-[13px] leading-[18px] text-light-text-primary dark:text-[#F5F6F8]">
                    {buildPending ? 'Building…' : 'Build pairing code'}
                  </Text>
                </Pressable>
              </View>
            )}

            {handshakeError != null && (
              <Text className="font-sans text-[12px] leading-[16px] text-light-accent-rose dark:text-dark-accent-rose">
                {handshakeError}
              </Text>
            )}
          </View>
        ) : pairedLabel !== null ? (
          /* Confirmed-paired card. `onWatchForPaired` resolved with
             the new device's label after polling
             `/v1/auth/sessions` until the BE recorded the
             registration. Replaces the "Pairing…" copy so the user
             gets immediate visual confirmation without having to
             switch to the Sessions tab. */
          <View
            className="
              flex flex-col items-center gap-3 mt-1
              p-4 rounded-[16px]
              bg-light-surface-primary dark:bg-[#0F1117]
              border border-light-accent-success/30 dark:border-[#1F4D2A]
            "
          >
            <View className="flex flex-row items-center gap-2">
              <CheckCircle2 size={16} strokeWidth={2} color="#3CCB4F" />
              <Text className="font-sans font-semibold text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">
                Paired
              </Text>
            </View>
            <Text className="font-sans text-[13px] leading-[18px] text-light-text-secondary dark:text-[#B5BAC3] text-center max-w-[280px]">
              <Text className="font-semibold">{pairedLabel}</Text>
              <Text> is now linked to your account. You can manage it from </Text>
              <Text className="font-semibold">Sessions</Text>
              <Text>.</Text>
            </Text>
          </View>
        ) : (
          /* Pairing-in-progress card. The sealed payload was
             uploaded to the BE and (when ws-fanout is reachable)
             pushed straight to the new device — the user should
             see this card briefly and the new device should
             auto-complete. The fallback pairing-code QR + text
             stay available behind a "Having trouble?" disclosure
             for environments where the push can't be delivered
             (no WebSocket upgrade, deploy lag, etc.). */
          <View
            className="
              flex flex-col items-center gap-3 mt-1
              p-4 rounded-[16px]
              bg-light-surface-primary dark:bg-[#0F1117]
              border border-light-border-secondary dark:border-[#22242A]
            "
          >
            <View className="flex flex-row items-center gap-2">
              <CheckCircle2 size={16} strokeWidth={2} color="#3CCB4F" />
              <Text className="font-sans font-semibold text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">
                {pushUndelivered
                  ? 'Use the pairing code below'
                  : 'Pairing your new device…'}
              </Text>
            </View>
            <Text className="font-sans text-[12px] leading-[16px] text-light-text-secondary dark:text-[#B5BAC3] text-center max-w-[280px]">
              {pushUndelivered
                ? "We couldn't reach the new device automatically — scan or paste the pairing code below."
                : (
                  <>
                    The new device will appear in Sessions once it confirms. This
                    pairing expires at <Text className="font-semibold">{expiresLabel}</Text>.
                  </>
                )}
            </Text>

            {!fallbackOpen ? (
              <Pressable
                onPress={() => setShowFallbackCode(true)}
                accessibilityRole="button"
                accessibilityLabel="ping_linking_show_fallback"
                className="self-center"
              >
                <Text className="font-sans text-[12px] leading-[16px] text-light-text-tertiary dark:text-[#6B7280] underline">
                  Having trouble? Show pairing code
                </Text>
              </Pressable>
            ) : (
              <View className="flex flex-col items-center gap-3 self-stretch">
                {renderQrCode != null && (
                  <View className="p-3 rounded-[12px] bg-white">
                    {renderQrCode(encodeTicketQr(buildResult.ticketId), 200)}
                  </View>
                )}
                <Text className="font-sans text-[12px] leading-[16px] text-light-text-secondary dark:text-[#B5BAC3] text-center max-w-[280px]">
                  Point your new device's camera at this code, or paste the
                  pairing code below into the new device.
                </Text>
                <View
                  className="
                    flex flex-row items-center gap-2 self-stretch
                    px-3 py-2 rounded-[8px]
                    bg-light-surface-secondary dark:bg-[#15171C]
                    border border-light-border-secondary dark:border-[#2F333B]
                  "
                >
                  <QrCode size={14} strokeWidth={1.75} color="#6B7280" />
                  <Text
                    selectable
                    numberOfLines={1}
                    ellipsizeMode="middle"
                    className="font-mono text-[12px] leading-[16px] text-light-text-secondary dark:text-[#B5BAC3] flex-1"
                  >
                    {buildResult.ticketId}
                  </Text>
                </View>
              </View>
            )}

            <Pressable
              onPress={() => {
                setBuildResult(null);
                setShowFallbackCode(false);
              }}
              accessibilityRole="button"
              accessibilityLabel="ping_linking_build_another"
            >
              <Text className="font-sans text-[12px] leading-[16px] text-light-text-tertiary dark:text-[#6B7280] underline">
                Link another device
              </Text>
            </Pressable>
          </View>
        )}
      </View>

      {onAcceptTicket != null && (
        <>
          <SettingsDivider />
          {/* New-device side — kept here for symmetry, but the
              auth-flow `LinkDevicePairingPane` is the canonical home
              for this half of the flow. Hosts that don't mount the
              pre-auth route can still complete pairing from Settings. */}
          <View testID="settings-linking-redeem" className="flex flex-col gap-3">
            <Text className="font-sans font-semibold text-[15px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">
              Redeem a pairing code
            </Text>
            <Text className="font-sans text-[13px] leading-[18px] text-light-text-secondary dark:text-[#B5BAC3]">
              Scan the pairing code shown on the existing device, or paste it below.
            </Text>
            {onScanRequest != null && (
              <Pressable
                onPress={handleScanForTicket}
                disabled={acceptPending}
                accessibilityRole="button"
                accessibilityLabel="ping_linking_scan_ticket"
                className="
                  flex flex-row items-center justify-center gap-2
                  py-3 rounded-[12px]
                  bg-light-accent-brand dark:bg-dark-accent-brand
                  active:opacity-80
                "
              >
                <ScanLine size={18} strokeWidth={2} color="#FFFFFF" />
                <Text className="font-sans font-semibold text-[14px] leading-[20px] text-light-text-onAccent dark:text-dark-text-onAccent">
                  {acceptPending ? 'Redeeming…' : 'Scan pairing code'}
                </Text>
              </Pressable>
            )}
            <View className="flex flex-col gap-2">
              <Text className="font-sans text-[11px] leading-[15px] text-light-text-tertiary dark:text-[#6B7280]">
                Or paste pairing code
              </Text>
              <View className="flex flex-row gap-2">
                <TextInput
                  value={ticketIdRaw}
                  onChangeText={setTicketIdRaw}
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholder="Pairing code"
                  placeholderTextColor="#6B7280"
                  className="
                    flex-1 font-mono text-[12px] leading-[16px]
                    px-3 py-2 rounded-[8px]
                    bg-light-surface-primary dark:bg-[#0F1117]
                    border border-light-border-secondary dark:border-[#22242A]
                    text-light-text-primary dark:text-[#F5F6F8]
                  "
                />
                <Pressable
                  onPress={handlePasteTicket}
                  disabled={acceptPending || ticketIdRaw.length === 0}
                  accessibilityRole="button"
                  accessibilityLabel="ping_linking_redeem"
                  className="
                    flex flex-row items-center gap-2
                    px-4 py-2 rounded-[8px]
                    bg-light-surface-secondary dark:bg-[#15171C]
                    border border-light-border-secondary dark:border-[#2F333B]
                    active:opacity-70
                  "
                >
                  <ClipboardPaste size={14} strokeWidth={1.75} color="#B5BAC3" />
                  <Text className="font-sans font-medium text-[13px] leading-[18px] text-light-text-primary dark:text-[#F5F6F8]">
                    Redeem
                  </Text>
                </Pressable>
              </View>
            </View>
            {acceptError != null && (
              <Text className="font-sans text-[12px] leading-[16px] text-light-accent-rose dark:text-dark-accent-rose">
                {acceptError}
              </Text>
            )}
            {acceptDone && (
              <View className="flex flex-row items-center gap-2 mt-1">
                <CheckCircle2 size={14} strokeWidth={2} color="#3CCB4F" />
                <Text className="font-sans text-[12px] leading-[16px] text-light-accent-success dark:text-dark-accent-success">
                  Linked. The new device will appear in Sessions on the existing device shortly.
                </Text>
              </View>
            )}
          </View>
        </>
      )}
    </View>
  );
}
